import { useState } from "react";
import { toast } from "react-hot-toast";
import {
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaClock,
  FaPaperPlane,
} from "react-icons/fa";
import ModernMap from "../../components/ModernMap";

const Contact = ({ embedded = false }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const mapPosition = [27.7172, 85.324];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success("Message sent — we'll get back to you soon.");
    setFormData({
      name: "",
      email: "",
      subject: "",
      message: "",
    });
  };

  const contactMethods = [
    {
      icon: FaPhone,
      title: "Call",
      details: "+977 980-000-1234",
      description: "Mon–Fri, 9am–6pm",
    },
    {
      icon: FaEnvelope,
      title: "Email",
      details: "hello@fixmyroad.com",
      description: "Usually within 24 hours",
    },
    {
      icon: FaMapMarkerAlt,
      title: "Visit",
      details: "Kathmandu, Nepal",
      description: "Road Safety Avenue",
    },
    {
      icon: FaClock,
      title: "Hours",
      details: "Monday – Friday",
      description: "9:00 AM – 5:00 PM",
    },
  ];

  return (
    <div className={embedded ? "pb-16 pt-6" : "min-h-screen pb-16"}>
      {!embedded && (
        <section className="relative overflow-hidden bg-gradient-to-br from-navy-950 via-navy-900 to-navy-700 px-4 py-14 text-white">
          <div className="pointer-events-none absolute left-10 top-8 h-40 w-40 rounded-full bg-amber-400/20 blur-3xl" />
          <div className="relative mx-auto max-w-3xl text-center">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-amber-400">
              Contact
            </p>
            <h1 className="font-display text-4xl font-bold md:text-5xl">
              Get in touch
            </h1>
            <p className="mx-auto mt-3 max-w-xl text-white/75">
              Questions, partnerships, or support — drop a note and we&apos;ll
              help you keep local roads safer.
            </p>
          </div>
        </section>
      )}

      {embedded && (
        <div className="mx-auto mb-8 max-w-3xl px-4 text-center">
          <p className="mb-2 text-sm font-semibold uppercase tracking-[0.18em] text-amber-600">
            Contact
          </p>
          <h2 className="font-display text-3xl font-bold text-navy-900 md:text-4xl">
            Get in touch
          </h2>
          <p className="mt-2 text-navy-800/70">
            Questions or support — we&apos;re here to help.
          </p>
        </div>
      )}

      <div
        className={`mx-auto max-w-6xl px-4 ${
          embedded ? "" : "relative z-10 pb-4 pt-6 sm:pt-8"
        }`}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {contactMethods.map(({ icon: Icon, title, details, description }) => (
            <div
              key={title}
              className="rounded-2xl border border-navy-900/10 bg-white p-5 shadow-[0_12px_36px_rgba(11,31,68,0.08)]"
            >
              <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/15 text-amber-600">
                <Icon />
              </div>
              <h3 className="font-display text-lg font-semibold text-navy-900">
                {title}
              </h3>
              <p className="mt-1 text-sm font-medium text-navy-800">{details}</p>
              <p className="text-sm text-navy-800/60">{description}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="rounded-3xl border border-navy-900/10 bg-white p-8 shadow-[0_20px_50px_rgba(11,31,68,0.08)] md:p-10">
            <h2 className="font-display text-2xl font-bold text-navy-900">
              Send a message
            </h2>
            <p className="mt-1 text-navy-800/65">
              We typically reply within one business day.
            </p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label
                    htmlFor="name"
                    className="mb-1.5 block text-sm font-medium text-navy-900"
                  >
                    Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-xl border border-navy-900/10 bg-surface px-4 py-2.5 outline-none focus:border-navy-700 focus:ring-2 focus:ring-navy-700/15"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="mb-1.5 block text-sm font-medium text-navy-900"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-xl border border-navy-900/10 bg-surface px-4 py-2.5 outline-none focus:border-navy-700 focus:ring-2 focus:ring-navy-700/15"
                    placeholder="you@email.com"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="subject"
                  className="mb-1.5 block text-sm font-medium text-navy-900"
                >
                  Subject
                </label>
                <input
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-xl border border-navy-900/10 bg-surface px-4 py-2.5 outline-none focus:border-navy-700 focus:ring-2 focus:ring-navy-700/15"
                  placeholder="How can we help?"
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="mb-1.5 block text-sm font-medium text-navy-900"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="5"
                  required
                  className="w-full rounded-xl border border-navy-900/10 bg-surface px-4 py-3 outline-none focus:border-navy-700 focus:ring-2 focus:ring-navy-700/15"
                  placeholder="Write your message..."
                />
              </div>

              <button
                type="submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-3 font-semibold text-navy-950 transition hover:bg-amber-400"
              >
                <FaPaperPlane />
                Send message
              </button>
            </form>
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-3xl bg-gradient-to-b from-navy-900 to-navy-800 p-8 text-white shadow-lg">
              <h3 className="font-display text-xl font-semibold">
                Quick notes
              </h3>
              <ul className="mt-5 space-y-4 text-sm text-white/75">
                <li className="rounded-xl bg-white/5 p-4">
                  Urgent road danger? Contact local authorities first.
                </li>
                <li className="rounded-xl bg-white/5 p-4">
                  Partnerships:{" "}
                  <span className="text-amber-300">partner@fixmyroad.com</span>
                </li>
                <li className="rounded-xl bg-white/5 p-4">
                  Product feedback is welcome on the Feedback page.
                </li>
              </ul>
            </div>

            <div className="overflow-hidden rounded-3xl border border-navy-900/10 bg-white shadow-[0_16px_40px_rgba(11,31,68,0.1)]">
              <div className="relative h-72">
                <ModernMap
                  center={mapPosition}
                  zoom={14}
                  height="100%"
                  markers={[
                    {
                      id: "hq",
                      position: mapPosition,
                      color: "#f59e0b",
                      popup: (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">
                            FixMyRoad
                          </p>
                          <p className="mt-1 font-semibold text-navy-900">
                            Kathmandu hub
                          </p>
                          <p className="text-xs text-navy-800/65">
                            Road Safety Avenue
                          </p>
                        </div>
                      ),
                    },
                  ]}
                />
                <div className="pointer-events-none absolute left-3 top-3 rounded-full bg-navy-950/75 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
                  Live map
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 border-t border-navy-900/8 p-4">
                <div>
                  <p className="font-medium text-navy-900">Visit our hub</p>
                  <p className="text-sm text-navy-800/65">Kathmandu, Nepal</p>
                </div>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${mapPosition[0]},${mapPosition[1]}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-xl bg-amber-500 px-3 py-2 text-sm font-semibold text-navy-950 transition hover:bg-amber-400"
                >
                  Directions
                </a>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Contact;
